import { SyntheticEvent, useEffect, useState } from 'react';
import { Box } from '@mui/system';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, CircularProgress, Grid, MenuItem, Tooltip, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AcceptTermsandConditionsDto, CreateCompanyOnboardingDTO, UserRegistrationV2Dto } from '@inosio/payments-sdk';
import { useStyles } from '../../style/Pages/BasicCompanyInfo';
import { schemaValidationBasicCompany, schemaValidationBasicCompanyType } from '../../utils/validation';
import { InputField } from '../../components/InputController';
import { getInosioTermsAndConditions, getRecordAgreementDetails, getStartOnboardingApplication, selectOnboardingApplicationState } from './sagas/pmonboardingSlice';
import { clearCredentials, getPasswordLogin, getRegisterUser, selectAuthState } from '../../shared/sagas/auth/authSlice';
import { approvedISOStandards } from '../../shared/util/approvedISOStandards';
import CustomDropdown from '../../components/customDropdown';
import TermsDialog from '../../components/TermsDialog';
import { IPA_ACCESS_TOKEN_NAME } from '../../utils/constants';
import { PhoneNumberInput } from '../../shared/PhoneNumberInput';
import { E164PhoneRegex } from '../../utils/regex';

//this is for the accordian jazz
const enum AccordianStates {
  CompanyInfo = 'Company Info',
  UserInfo = 'User Info',
}

//represents which step we are on so we can orchestrate accordingly.
const enum RegistrationSteps {
  RegisteringUser,
  CreatingCompany,
  ReceivingUpdatedToken,
}

export const BasicCompanyInfo = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  const initialValues: schemaValidationBasicCompanyType = {
    company_legal_name: '',
    company_email: '',
    company_physical_address_line1: '',
    city: '',
    state: approvedStates[0].Code, // defaults to Alabama
    zip: '',
    country: 'US', // defaults to US
    website: '',
    firstName: '',
    lastName: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
  };

  //local states
  const [accordianState, setAccordianState] = useState<AccordianStates | false>(AccordianStates.CompanyInfo);
  const [errorMessageState, setErrorMessageState] = useState<string>('');
  const [openInosioTermsDialog, setOpenInosioTermsDialog] = useState(false);
  const [BasicCompanyInfoFormValues, setFormValues] = useState(initialValues);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState(approvedStates[0].Code);
  const [isUserInfoAreaActive, setIsUserInfoAreaActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(RegistrationSteps.RegisteringUser);

  //selectors
  const { userRegistrationError, isFetching, isSuccess: isAuthSuccess, isError: isAuthError } = useSelector(selectAuthState);
  const { contract, isSuccess: isOnboardingRequestSuccess, isError: isOnboardingRequestError } = useSelector(selectOnboardingApplicationState);

  const handleAccordionChange = (panel: AccordianStates) => (event: SyntheticEvent, isExpanded: boolean) => {
    setAccordianState(isExpanded ? panel : false);
  };

  //step 2, when user accepts the TsCs, then register them as a generic Inosio User
  const handleCloseInosio = () => {
    setOpenInosioTermsDialog(false);
    //Register a user after accepting the Inosio TCs.
    registerUser();
  };

  const resetStateSelection = () => {
    formik.setFieldValue('state', '');
    formik.setFieldValue('state_of_incorporation', '');
    setSelectedState('');
  };

  const formik = useFormik<schemaValidationBasicCompanyType>({
    initialValues,
    validationSchema: schemaValidationBasicCompany,

    //step 1, open the Inosio TCs dialog
    onSubmit: async (values) => {
      setFormValues(values);
      //Open Inosio TCs modal
      setOpenInosioTermsDialog(true);
      return;
    },
  });

  const registerUser = () => {
    // prevent duplicate auth requests
    !sessionStorage.getItem(IPA_ACCESS_TOKEN_NAME) &&
      dispatch({
        type: getRegisterUser.type,
        payload: {
          FirstName: BasicCompanyInfoFormValues.firstName,
          LastName: BasicCompanyInfoFormValues.lastName,
          Email: BasicCompanyInfoFormValues.email,
          Password: BasicCompanyInfoFormValues.password,
        } as UserRegistrationV2Dto,
      });
  };

  //step 3, if user's account was successfully created, create an onboarding application
  useEffect(() => {
    if (isAuthError) {
      setErrorMessageState(userRegistrationError?.message! || '');
    }

    if (isAuthSuccess && currentStep === RegistrationSteps.RegisteringUser) {
      setCurrentStep(RegistrationSteps.CreatingCompany);

      //create onboarding app
      dispatch({
        type: getStartOnboardingApplication.type,
        payload: {
          LegalName: BasicCompanyInfoFormValues.company_legal_name,
          Website: BasicCompanyInfoFormValues.website,
          PhoneNumber: BasicCompanyInfoFormValues.phone_number,
          Email: BasicCompanyInfoFormValues.company_email,
          PhysicalAddress: {
            Line1: BasicCompanyInfoFormValues.company_physical_address_line1,
            City: BasicCompanyInfoFormValues.city,
            State: BasicCompanyInfoFormValues.state,
            Zip: BasicCompanyInfoFormValues.zip,
          },
        } as CreateCompanyOnboardingDTO,
      });

      //record user tscs
      dispatch({
        type: getRecordAgreementDetails.type,
        payload: {
          Version: contract.InosioTsCs.Version,
          Description: contract.InosioTsCs.Description,
        } as AcceptTermsandConditionsDto,
      });
    }
  }, [dispatch, isAuthSuccess, isAuthError, currentStep, BasicCompanyInfoFormValues, errorMessageState, userRegistrationError]);

  //step 4, once the onboarding app has been started, we need to relog so we get our PM admin token (that was just created)
  useEffect(() => {
    if(isOnboardingRequestError){}

    if(isOnboardingRequestSuccess && currentStep === RegistrationSteps.CreatingCompany){
      //get updated access token with pm profile
      dispatch({
        type: getPasswordLogin.type,
        payload: {
          email: BasicCompanyInfoFormValues.email,
          password: BasicCompanyInfoFormValues.password,
        },
      });
      setCurrentStep(RegistrationSteps.ReceivingUpdatedToken);
    }
  }, [dispatch, isOnboardingRequestSuccess, currentStep, isOnboardingRequestError]);

  //step 5, once we got relogged, then move on to the wizard page.
  useEffect(() => {
    if (isAuthSuccess && currentStep === RegistrationSteps.ReceivingUpdatedToken) {
      history.push('/onboarding/wizard');
    }
  }, [isAuthSuccess, isAuthError, currentStep, history]);

  // only fresh applications are permitted on this page
  useEffect(() => {
    dispatch(clearCredentials());
    dispatch({ type: getInosioTermsAndConditions.type });
  }, [dispatch]);

  useEffect(() => {
    const { company_legal_name, company_email, phone_number, website, company_physical_address_line1, city, state, zip, country } = formik.values;
    if (company_legal_name && company_email && phone_number && phone_number.match(E164PhoneRegex) && website && company_physical_address_line1 && city && state && zip && country) {
      setIsUserInfoAreaActive(true);
    } else {
      setIsUserInfoAreaActive(false);
    }
  }, [formik]);

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7}>
      {errorMessageState?.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Alert severity="error">{userRegistrationError?.message}</Alert>
        </Box>
      )}

      <Box component="form" noValidate onSubmit={formik.handleSubmit} className={classes.centerLabel}>
        <Accordion
          classes={{ root: classes.accordionRoot }}
          sx={{ borderBottom: 'none' }}
          expanded={accordianState === AccordianStates.CompanyInfo}
          onChange={handleAccordionChange(AccordianStates.CompanyInfo)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '18px',
              }}
              className={classes.companyInfo}
            >
              General Company Info
              <Tooltip title="This information is used for displays, receipts for your residents, and approving your payment processing application." placement="right">
                <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer', ml: '5px' }} />
              </Tooltip>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
            <Box sx={{ maxWidth: 340 }}>
              <InputField
                name={'company_legal_name'}
                placeholder={'Company Name'}
                label={'Company Name'}
                isError={false}
                errors={formik.errors?.company_legal_name}
                touched={formik.touched?.company_legal_name}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('company_legal_name', e.target.value)}
              />
              <InputField
                name={'company_email'}
                placeholder={'Company Email'}
                label={'Company Email'}
                isError={false}
                errors={formik.errors?.company_email}
                touched={formik.touched?.company_email}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('company_email', e.target.value)}
              />
              <PhoneNumberInput
                {...formik.getFieldProps('phone_number')}
                label="Phone Number"
                showErrorMessage={!!formik.errors?.phone_number && !!formik.touched?.phone_number}
                onChange={(phone) => {
                  formik.setFieldValue('phone_number', phone.startsWith('+') ? phone : `+${phone}`);
                }}
                errorMessage={formik.errors?.phone_number}
              />
              {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
              <Box sx={{ marginTop: '16px' }}></Box>
              <InputField
                name={'website'}
                placeholder={'Website'}
                label={'Website'}
                isError={false}
                errors={formik.errors?.website}
                touched={formik.touched?.website}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('website', e.target.value)}
              />
              <InputField
                name={'company_physical_address_line1'}
                placeholder={'Company Physical Address '}
                label={'Company Physical Address'}
                isError={false}
                errors={formik.errors?.company_physical_address_line1}
                touched={formik.touched?.company_physical_address_line1}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('company_physical_address_line1', e.target.value)}
              />
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} sm={6} id="city-input">
                  <InputField
                    name={'city'}
                    placeholder={'City'}
                    label={'City'}
                    isError={false}
                    errors={formik.errors?.city}
                    touched={formik.touched?.city}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} id="states-dropdown">
                  {selectedCountry && ['US', 'CA'].includes(selectedCountry) ? (
                    <>
                      <CustomDropdown
                        id={'state-dropdown'}
                        isFullWidth
                        label={'State / Province'}
                        labelId={''}
                        value={selectedState}
                        handleChange={(e) => {
                          setSelectedState(e.target.value);
                          formik.setFieldValue('state', e.target.value);
                        }}
                      >
                        {selectedCountry === 'US'
                          ? approvedStates.map((stateRecord) => (
                              <MenuItem style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                                {stateRecord.State + ' - ' + stateRecord.Code}
                              </MenuItem>
                            ))
                          : approvedProvinces.map((provinceRecord) => (
                              <MenuItem style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                                {provinceRecord.Province + ' - ' + provinceRecord.Code}
                              </MenuItem>
                            ))}{' '}
                      </CustomDropdown>
                    </>
                  ) : (
                    <InputField
                      value={formik.values.state}
                      name={'state'}
                      isError={false}
                      errors={formik.errors?.state}
                      touched={formik.touched?.state}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue('state', e.target.value)}
                      placeholder={'State / Province'}
                      label={'State / Province'}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} sm={6} id="zip-input">
                  <InputField
                    name={'zip'}
                    placeholder={'Postal Code'}
                    label={'Postal Code'}
                    isError={false}
                    errors={formik.errors?.zip}
                    touched={formik.touched?.zip}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('zip', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} id="countries-dropdown">
                  <CustomDropdown
                    label={'Country'}
                    isFullWidth
                    handleChange={(e: any) => {
                      resetStateSelection();

                      // update selected country
                      setSelectedCountry(e.target.value);
                      formik.setFieldValue('country', e.target.value);
                    }}
                    value={selectedCountry}
                  >
                    {approvedCountries.map((countryRecord) => (
                      <MenuItem style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                        {countryRecord.Country + ' - ' + countryRecord.Code}
                      </MenuItem>
                    ))}
                  </CustomDropdown>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          disabled={!isUserInfoAreaActive}
          classes={{ root: classes.accordionRoot }}
          expanded={accordianState === AccordianStates.UserInfo}
          onChange={handleAccordionChange(AccordianStates.UserInfo)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2bh-content" id="panel2bh-header">
            <Typography
              className={classes.companyInfo}
              style={{
                fontWeight: 600,
                fontSize: '18px',
              }}
            >
              User Info
              <Tooltip title="This information is only used to create your account in our system and is not shared with residents." placement="right">
                <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer', ml: '5px' }} />
              </Tooltip>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
            <Box sx={{ maxWidth: 340 }}>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} sm={6} id="firstname-input">
                  <InputField
                    name={'firstName'}
                    label={'First name'}
                    isError={false}
                    errors={formik.errors?.firstName}
                    touched={formik.touched?.firstName}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('firstName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} id="lastname-input">
                  <InputField
                    name={'lastName'}
                    errors={formik.errors?.lastName}
                    touched={formik.touched?.lastName}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('lastName', e.target.value)}
                    label={'Last name'}
                  />
                </Grid>
              </Grid>

              <InputField
                name={'email'}
                placeholder={'Email'}
                label={'Email'}
                isError={false}
                errors={formik.errors?.email}
                touched={formik.touched?.email}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('email', e.target.value)}
              />
              <InputField
                name={'password'}
                placeholder={'Password'}
                type="password"
                label={'Password'}
                isError={false}
                errors={formik.errors?.password}
                touched={formik.touched?.password}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('password', e.target.value)}
              />
              <InputField
                name={'confirmPassword'}
                type="password"
                label={'Confirm password'}
                isError={false}
                errors={formik.errors?.confirmPassword}
                touched={formik.touched?.confirmPassword}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('confirmPassword', e.target.value)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Box style={{ width: '100%' }} sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '26px' }}>
          <div className={classes.wrapper}>
            <Button type="submit" disabled={Boolean(!formik.isValid)} className={classes.signUpBtn}>
              <Typography className={classes.signUpBtnText}>Sign Up</Typography>
            </Button>
            {isFetching && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Box>
      </Box>

      <TermsDialog
        open={openInosioTermsDialog}
        scroll={true}
        text={contract.InosioTsCs.Text}
        accpetText={`I am authorized to enter into agreements/contracts on behalf of my company.`}
        title="Inosio Agreement"
        handleConfirm={handleCloseInosio}
        buttonText="Agree"
      />
    </Grid>
  );
};
